import { DoublyLinkedList } from './doubly-linked-list.js'

/**
 * Pila (Stack) — estructura LIFO (Last In, First Out).
 * El último elemento en entrar es el primero en salir.
 * Acepta cualquier valor: primitivos u objetos.
 * @template T - El tipo de los valores almacenados en la pila.
 */
export class Stack<T> {
  readonly #list = new DoublyLinkedList<T>()

  /**
   * Agrega un valor al tope de la pila.
   * @param value - El valor a agregar (primitivo u objeto).
   */
  push(value: T): void {
    this.#list.push(value)
  }

  /**
   * Elimina y retorna el valor del tope de la pila.
   * @returns El valor eliminado, o undefined si la pila está vacía.
   */
  pop(): T | undefined {
    return this.#list.pop()
  }

  /**
   * Retorna el valor del tope sin eliminarlo.
   * @returns El valor del tope, o undefined si la pila está vacía.
   */
  peek(): T | undefined {
    return this.#list.tail?.value
  }

  /**
   * Indica si la pila está vacía.
   */
  isEmpty(): boolean {
    return this.#list.isEmpty()
  }

  /**
   * Cantidad de elementos en la pila.
   */
  get size(): number {
    return this.#list.size
  }

  /**
   * Retorna un array con todos los valores de la pila, de base a tope.
   */
  toArray(): T[] {
    return this.#list.toArray()
  }

  /**
   * Vacía la pila completa.
   */
  clear(): void {
    this.#list.clear()
  }

  /**
   * Hace la pila iterable con for...of, spread y destructuring.
   */
  *[Symbol.iterator](): Iterator<T> {
    yield* this.#list
  }
}